import { Calendar, Clock, MapPin, Music, ExternalLink } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import PageSeo from "@/components/PageSeo";

const EventsPage = () => {
  const { data: events = [], isLoading } = useQuery({
    queryKey: ["events-all"],
    queryFn: async () => {
      const today = new Date().toISOString().split("T")[0];
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("is_active", true)
        .gte("date", today)
        .order("date", { ascending: true })
        .order("time", { ascending: true, nullsFirst: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const eventSchemas = events.map((event: any) => ({
    "@context": "https://schema.org",
    "@type": "Event",
    name: event.title,
    startDate: event.time ? `${event.date}T${event.time}` : event.date,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: event.location || "Luther Club Kaiserslautern",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Martin-Luther-Straße 8",
        addressLocality: "Kaiserslautern",
        postalCode: "67657",
        addressCountry: "DE",
      },
    },
    ...(event.image_url ? { image: event.image_url } : {}),
    ...(event.description ? { description: event.description } : {}),
    ...(event.dj ? { performer: { "@type": "PerformingGroup", name: event.dj } } : {}),
    organizer: { "@type": "Organization", name: "Luther Club", url: "https://lutherclub.de" },
  }));

  return (
    <div className="pt-20">
      <PageSeo
        title="Events – Luther Club Kaiserslautern"
        description="Alle kommenden Events und Partys im Luther Club Kaiserslautern. DJs, Themenabende und Specials – jetzt entdecken."
        path="/events"
        jsonLd={eventSchemas}
      />
      {/* Hero */}
      <div className="relative py-20 px-4 text-center">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(184,164,76,0.06)_0%,_transparent_70%)]" />
        <h1 className="relative text-4xl md:text-5xl font-bold text-primary mb-3">
          Events
        </h1>
        <p className="relative text-lg text-foreground/70 italic">
          Unsere kommenden Veranstaltungen
        </p>
      </div>

      {/* Events */}
      <div className="max-w-5xl mx-auto px-4 pb-24">
        {isLoading ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground">Events werden geladen…</p>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20 border border-border/30 rounded">
            <Calendar className="w-12 h-12 text-primary/30 mx-auto mb-4" />
            <h2 className="font-[Montserrat] text-xl tracking-[0.15em] uppercase text-foreground/60 mb-2">
              Keine bevorstehenden Veranstaltungen
            </h2>
            <p className="text-muted-foreground">
              Schau bald wieder vorbei – neue Events werden hier angekündigt!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {events.map((event, index) => (
              <div
                key={event.id}
                id={`event-${event.id}`}
                className={`border border-border/40 rounded-lg overflow-hidden bg-card/50 hover:border-primary/30 transition-colors scroll-mt-24 ${
                  index % 3 === 0 ? "md:col-span-2" : ""
                }`}
              >
                <div className={`flex ${index % 3 === 0 ? "flex-col md:flex-row" : "flex-col"}`}>
                  {/* Image */}
                  <div
                    className={`${
                      index % 3 === 0
                        ? "aspect-[4/5] md:aspect-auto md:w-1/3 md:min-h-[300px]"
                        : "aspect-[4/5]"
                    } bg-muted/20 relative overflow-hidden`}
                  >
                    {event.image_url ? (
                      <img
                        src={event.image_url}
                        alt={event.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center min-h-[200px]">
                        <span className="font-[Montserrat] text-[10px] text-muted-foreground tracking-wider uppercase">
                          Event-Foto
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6 flex-1">
                    {(() => {
                      const d = new Date(event.date + "T00:00:00");
                      const weekday = d.toLocaleDateString("de-DE", { weekday: "short" }).replace(".", "").toUpperCase();
                      const date = d.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
                      return (
                        <div className="mb-4 inline-flex items-center gap-2 border-l-2 border-primary pl-3 py-1">
                          <span className="font-[Montserrat] text-[10px] tracking-[0.18em] uppercase text-primary/70">{weekday}</span>
                          <span className="font-[Montserrat] text-sm md:text-base tracking-[0.08em] uppercase text-primary">{date}</span>
                        </div>
                      );
                    })()}
                    <h3 className="font-[Montserrat] text-lg tracking-[0.1em] uppercase text-primary mb-3">
                      {event.title}
                    </h3>
                    <div className="flex flex-wrap gap-4 text-sm text-foreground/70 mb-3">
                      {event.time && (
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4 text-primary/50" />
                          {event.time}
                        </span>
                      )}
                      {event.location && (
                        <span className="flex items-center gap-1.5">
                          <MapPin className="w-4 h-4 text-primary/50" />
                          {event.location}
                        </span>
                      )}
                      {event.dj && (
                        <span className="flex items-center gap-1.5">
                          <Music className="w-4 h-4 text-primary/50" />
                          {event.dj}
                        </span>
                      )}
                    </div>
                    <p className="text-foreground/80 whitespace-pre-line">{event.description}</p>
                    {(event as any).link_url && (
                      <a
                        href={(event as any).link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 mt-3 text-primary hover:text-primary/80 transition-colors text-sm font-medium"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {(event as any).link_text || (event as any).link_url}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
